/**
 * @file Hook de vérification de l'exigence d'empreinte bancaire.
 * @module packages.common.components.table-reservation.hooks.usePaymentRequirement
 *
 * Interroge l'API `evaluateCardHold` dès que les critères de réservation sont
 * complets (service, date, créneau, convives, et éventuellement formules
 * sélectionnées). L'API tranche côté serveur en combinant la politique
 * globale du restaurant et les politiques portées par les formules
 * sélectionnées, puis retourne les caractéristiques de l'empreinte (montant,
 * source, deadline) **sans créer d'intent Stripe**. La création de l'intent
 * est différée à l'entrée de `PaymentStep` via `usePreparePayment`, afin
 * d'éviter de polluer Stripe avec des intents orphelins à chaque clic.
 *
 * Deux contraintes architecturales :
 *
 * 1. **Cohérence avec le backend** — la source de vérité sur l'exigence
 *    d'empreinte est le serveur. Le hook ne tente jamais de deviner côté
 *    client ; il se contente de refléter la dernière réponse de l'API dans
 *    `formData.paymentRequired`, ce qui pilote automatiquement l'apparition
 *    de l'étape PAYMENT dans le stepper.
 *
 * 2. **Latest-wins via `requestIdRef`** — l'utilisateur peut ajuster
 *    rapidement les quantités sur `FormulaStep`, enchaînant plusieurs
 *    requêtes. Seule la réponse de la dernière requête émise met à jour
 *    l'état, ce qui évite qu'une réponse en retard n'écrase un résultat
 *    plus récent. Le cleanup des effets incrémente aussi `requestIdRef`
 *    pour neutraliser une requête en vol lors d'un démontage.
 */
import {useEffect, useMemo, useRef, useState} from 'react';
import {useReservationData} from 'packages/common/components/table-reservation/context/ReservationDataContext';
import {logger} from 'packages/common/utils/clientLogger';

/**
 * Formate une date (`Date` ou chaîne ISO) au format `YYYY-MM-DD` en timezone
 * locale du client. Renvoie `null` si la valeur n'est pas exploitable.
 *
 * @param {Date|string|null|undefined} value - Date à formater
 * @return {string|null} Date formatée, ou `null` si valeur invalide
 */
function toIsoDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (typeof value === 'string') return value;
  return null;
}

/**
 * Hook de vérification de l'exigence d'empreinte bancaire.
 *
 * Se déclenche automatiquement dès que service, date, créneau et convives
 * sont renseignés, puis à chaque changement de `selectedFormulas`.
 * Met à jour `formData.paymentRequired`, `paymentConfig`, `cardHoldAmount`
 * (et réinitialise tout paiement précédemment préparé).
 *
 * Bypass complet pour les inscriptions en liste d'attente.
 *
 * @param {Object} formData - Données du formulaire de réservation
 * @param {Function} updateFormData - Mutateur du formulaire (field, value)
 * @return {Object} État du hook — `isChecking` (vérification en cours),
 *     `checkError` (message d'erreur ou `null`).
 */
export function usePaymentRequirement(formData, updateFormData) {
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState(null);

  const {evaluateCardHold} = useReservationData();

  // Identifiant monotone de la dernière requête émise. Seule la réponse
  // portant cet identifiant est autorisée à mettre à jour l'état.
  const requestIdRef = useRef(0);

  // Sérialisation stable des formules sélectionnées pour servir de clé de
  // dépendance. Le tri garantit qu'un re-render sans changement fonctionnel
  // (ordre différent) ne relance pas de requête inutile.
  const selectedFormulasKey = useMemo(() => {
    const list = (formData.selectedFormulas || [])
        .map((f) => ({formulaId: f.formulaId, quantity: f.quantity || 1}))
        .sort((a, b) => String(a.formulaId).localeCompare(String(b.formulaId)));
    return JSON.stringify(list);
  }, [formData.selectedFormulas]);

  useEffect(() => {
    // Chaque nouveau cycle invalide toute requête en vol.
    const currentRequestId = ++requestIdRef.current;

    // Invalidation systématique des données Stripe préparées : un changement
    // de paramètre amont rend l'ancien `clientSecret` obsolète (montant ou
    // mode peut différer), donc on le drop tout de suite — `PaymentStep`
    // bascule en loader si l'utilisateur s'y rend pendant la vérif et le
    // préchargement régénèrera un intent correct dès que la nouvelle décision
    // sera connue.
    //
    // En revanche, `paymentRequired` n'est PAS remis à `false` ici : laisser
    // sa valeur précédente garde `availableSteps` stable pendant la fenêtre
    // de vérif (~200 ms), donc le bouton "Suivant" reste cliquable sans
    // risque de sauter l'étape PAYMENT par erreur. La nouvelle valeur est
    // appliquée uniquement à la réponse serveur (seule source de vérité).
    updateFormData('paymentMethodId', null);
    updateFormData('paymentIntentId', null);
    updateFormData('paymentConfig', null);
    // Une confirmation acquise ne vaut que pour l'intent qu'elle a consommé
    updateFormData('confirmedIntent', null);
    updateFormData('slotLost', null);
    setCheckError(null);

    // La liste d'attente ne requiert jamais d'empreinte. Contrairement à la
    // fenêtre de vérif transitoire ci-dessus, c'est un état terminal : on remet
    // donc `paymentRequired` à false pour retirer l'étape PAYMENT du stepper
    // (sinon elle resterait affichée à vide si le créneau précédent l'exigeait).
    if (formData.isWaitlistReservation) {
      updateFormData('cardHoldAmount', 0);
      updateFormData('paymentRequired', false);
      setIsChecking(false);
      return undefined;
    }

    // Toutes les dépendances sont requises avant l'appel API.
    if (!formData.service || !formData.partySize || !formData.date || !formData.timeSlot) {
      setIsChecking(false);
      return undefined;
    }

    const dateString = toIsoDate(formData.date);
    if (!dateString) {
      setIsChecking(false);
      return undefined;
    }

    const dateTimeString = `${dateString}T${formData.timeSlot}:00`;
    const selectedFormulas = JSON.parse(selectedFormulasKey);

    // Debounce court (100 ms) : sous le seuil de perception humaine
    // d'instantanéité, absorbe les bursts de +/- sur FormulaStep sans
    // dégrader la réactivité ressentie. L'endpoint `evaluateCardHold` étant
    // léger (~50-100 ms backend, pas d'intent Stripe), un debounce plus long
    // n'a plus de bénéfice ; le `requestIdRef` latest-wins suffit à neutraliser
    // les réponses tardives en cas de requêtes croisées.
    //
    // `setIsChecking(true)` est volontairement déplacé **à l'intérieur** du
    // timer : pendant la fenêtre de debounce (100 ms), `isChecking` reste à
    // `false` et le bouton "Suivant" demeure cliquable. Un utilisateur qui
    // clique très vite (< 100 ms après un changement de créneau ou de
    // formule) bascule à l'étape suivante instantanément ; la requête
    // serveur tourne en background et corrige `paymentRequired` au besoin.
    // Le `requestIdRef` neutralise les réponses obsolètes, et le préchargement
    // de l'intent Stripe à l'étape CONTACT garantit que `clientSecret` est
    // prêt à temps même si l'utilisateur a navigué en vitesse.
    const debounceTimer = setTimeout(() => {
      setIsChecking(true);
      (async () => {
        try {
          const paymentData = await evaluateCardHold({
            numberOfGuests: formData.partySize,
            dateTime: dateTimeString,
            service: formData.service,
            selectedFormulas: selectedFormulas.length > 0 ? selectedFormulas : undefined,
          });

          // Réponse périmée : une requête plus récente a été émise entre-temps.
          if (currentRequestId !== requestIdRef.current) return;

          if (paymentData?.success && paymentData.data?.required) {
            updateFormData('paymentRequired', true);
            updateFormData('cardHoldAmount', paymentData.data.amount);
            // `paymentConfig` ne porte que la décision à ce stade ; le
            // `clientSecret` et autres champs Stripe sont ajoutés par
            // `PaymentStep` au moment d'afficher le formulaire de carte.
            updateFormData('paymentConfig', paymentData.data);
          } else {
            updateFormData('paymentRequired', false);
            updateFormData('cardHoldAmount', 0);
          }
        } catch (err) {
          if (currentRequestId !== requestIdRef.current) return;
          logger.error('Erreur lors de la vérification du paiement:', err);
          setCheckError('Impossible de vérifier les exigences de paiement');
          // Dégradation gracieuse : on laisse le flow continuer sans empreinte
          // plutôt que de bloquer la réservation sur une erreur réseau transitoire.
          // Le backend rejettera la création si l'empreinte est indispensable.
          updateFormData('paymentRequired', false);
          updateFormData('cardHoldAmount', 0);
        } finally {
          if (currentRequestId === requestIdRef.current) {
            setIsChecking(false);
          }
        }
      })();
    }, 100);

    // Annule le timer si les deps changent dans la fenêtre de debounce
    // (= aucun appel Stripe inutile) et neutralise toute requête en vol.
    return () => {
      clearTimeout(debounceTimer);
      requestIdRef.current += 1;
    };
  }, [
    formData.service,
    formData.partySize,
    formData.date,
    formData.timeSlot,
    formData.isWaitlistReservation,
    selectedFormulasKey,
    evaluateCardHold,
    updateFormData,
  ]);

  return {isChecking, checkError};
}
